import React, { FunctionComponent, useEffect, useState, ChangeEvent, KeyboardEvent, useRef } from "react";
import { FIRST_NAME_PATH } from "../../routing/paths";
import { Spinner } from "../Spinner";
import PropTypes from "prop-types";
import { Invocations } from "../../containers/Wizard";
import { SubCategory } from "../../containers/BusinessStep/actions"
import ButtonOptions from "../ButtonOptions";
import { noNormalizer } from "../../shared/normalizers";

const NEW_BUSINESS_SUB_CAT_SPELL = "sub_category";

export type CategoriesStepProps = {
  loading: boolean;
  subCategories: Array<SubCategory>;
  invocations: Invocations;
  previous: string;
  getSubCategories: Function;
  invocate: Function;
  changePath: Function;
  validation: Function;
};

const CategoriesStep: FunctionComponent<CategoriesStepProps> = ({
  loading,
  previous,
  subCategories,
  getSubCategories,
  invocate,
  invocations,
  changePath,
  validation,
}: CategoriesStepProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [input, setInput] = useState<HTMLInputElement | null>();
  const [value, setValue] = useState("");
  const [categories, setCategories] = useState(subCategories);
  const [category, setCategory] = useState<SubCategory>();
  const [spinner, setSpinner] = useState(false);

  useEffect(() => {
    setCategories(subCategories);
  }, [subCategories, setCategories]);

  useEffect(() => {
    getSubCategories();
  }, [getSubCategories]);

  useEffect(() => {
    setSpinner(loading);
  }, [loading, setSpinner]);

  const handleSubmit = () => {
    if (!input?.reportValidity()) return;

    const invalid = validation ? validation(category?.uuid) : null;
    if (invalid) alert("Invalid category, please choose one from the list");
    else {
      invocate(NEW_BUSINESS_SUB_CAT_SPELL, category?.uuid, FIRST_NAME_PATH);

      if (category?.default_offer_text) {
        invocate("caption", category?.default_offer_text);
      } else {
        invocations["caption"] = "";
      }

      const backgroundImage = category?.default_image_filename ? category?.default_image_filename : invocations["default_background_image"];
      const layout = category?.default_layout ? `layout-${category.default_layout}` : "layout-1";

      invocate("background", backgroundImage);
      invocate("layout", layout);
    };
  };

  const handleCategory = (catUuid?: string) => {
    const selectedCategory = categories.find((category) => category.uuid === catUuid)
    setCategory(selectedCategory);
    setValue(noNormalizer(selectedCategory?.title || ""));
    setDropdownOpen(false);
  };

  const renderCategories = () => (
    categories.map((category) =>
      <p key={category.uuid} onClick={() => handleCategory(category.uuid)}>{category.title}</p>
    )
  );

  const onChange = (val: ChangeEvent<HTMLInputElement>) => {
    const filteredCats = subCategories.filter(category => category.title.toLowerCase().includes(noNormalizer(val.target.value.toLowerCase())))

    if (filteredCats.length > 0) setDropdownOpen(true)

    setCategories(filteredCats)
    setValue(noNormalizer(val.target.value));
  };

  const openDropdown = () => {
    setDropdownOpen(true)
  };

  const useOutsideAlerter = (ref: any) => {
    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (ref.current && !ref.current.contains(event.target)) {
          setDropdownOpen(false)
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  };

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") handleSubmit();
  };

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  return (
    spinner ? <Spinner /> :
    <div ref={wrapperRef} className="question">
      <h3>
        Please choose a <strong>category</strong>
      </h3>
      <h4>Search for a term that describes you best.</h4>
      <form>
        <div className="search-dropdown">
        <input
          id="cxategory-field"
          ref={(el) => setInput(el)}
          type="text"
          placeholder="Type your answer here..."
          autoComplete="off"
          value={value}
          onChange={onChange}
          onClick={openDropdown}
          onKeyPress={onKeyPress}
          required={true}
          autoFocus
        />
        {dropdownOpen &&
          <div className="dropdown-options">
            {renderCategories()}
          </div>
        }
        </div>
      </form>
      <ButtonOptions
        changePath={changePath}
        previous={previous}
        handleSubmit={handleSubmit}
      /> 
    </div>
  );
};

CategoriesStep.propTypes = {
  loading: PropTypes.bool.isRequired,
  subCategories: PropTypes.array.isRequired,
  getSubCategories: PropTypes.func.isRequired,
}

export default CategoriesStep;
